// @ts-check

/**
 * Depending on resolution and scaling factor, the track size and position
 * might not perfectly align. A small offset ensures buttons always update correctly.
 */
const ROUNDING_OFFSET = 2;

/**
 * @param {HTMLElement} host
 * @param {object} [options]
 * @param {string} [options.trackSelector]
 * @param {string} [options.slideSelector]
 * @param {string} [options.buttonPreviousSelector]
 * @param {string} [options.buttonNextSelector]
 * @param {string} [options.indicatorDotSelector]
 * @param {string} [options.navigationSelector]
 * @param {string} [options.navigationAnchorSelector]
 * @param {boolean} [options.disableNavAtBoundary]
 * @returns {() => void}
 */
export function initFinstralSlider(host, options = {}) {
	const {
		trackSelector = "[data-slider-track]",
		slideSelector = "[data-slider-slide]",
		buttonPreviousSelector = "[data-slider-btn-prev]",
		buttonNextSelector = "[data-slider-btn-next]",
		indicatorDotSelector = null,
		navigationSelector = null,
		navigationAnchorSelector = null,
		disableNavAtBoundary = false,
	} = options;

	const track = /** @type {HTMLElement} */ (host.querySelector(trackSelector));
	const slides = /** @type {HTMLElement[]} */ (
		Array.from(host.querySelectorAll(slideSelector))
	);
	const buttonPrevious = /** @type {HTMLButtonElement} */ (
		host.querySelector(buttonPreviousSelector)
	);
	const buttonNext = /** @type {HTMLButtonElement} */ (
		host.querySelector(buttonNextSelector)
	);
	const navigation = navigationSelector
		? /** @type {HTMLElement} */ (host.querySelector(navigationSelector))
		: null;
	const indicatorDots = indicatorDotSelector
		? /** @type {HTMLElement[]} */ (
				Array.from(host.querySelectorAll(indicatorDotSelector))
			)
		: null;

	const controller = new AbortController();
	const { signal } = controller;

	function getSlideSize() {
		return slides[0].offsetWidth;
	}

	function getTrackTotalSize() {
		return track.scrollWidth;
	}

	function getTrackVisibleSize() {
		return track.offsetWidth;
	}

	function getTrackPosition() {
		return track.scrollLeft;
	}

	if (disableNavAtBoundary) {
		buttonPrevious.removeAttribute("hidden");
		buttonNext.removeAttribute("hidden");
	}

	/**
	 * Disable or hide a nav button depending on the slider configuration.
	 * @param {HTMLButtonElement} button - Previous or next button.
	 * @param {boolean} isAtBoundary - Whether the track is at that button's end.
	 */
	function setNavButton(button, isAtBoundary) {
		if (disableNavAtBoundary) {
			if (button.disabled !== isAtBoundary) {
				button.disabled = isAtBoundary;
			}
		} else {
			button.toggleAttribute("hidden", isAtBoundary);
		}
	}

	function setButtonState() {
		const trackPosition = getTrackPosition();
		const trackVisibleSize = getTrackVisibleSize();
		const trackTotalSize = getTrackTotalSize();

		if (trackVisibleSize + ROUNDING_OFFSET >= trackTotalSize) {
			setNavButton(buttonPrevious, true);
			setNavButton(buttonNext, true);
			return;
		}

		if (trackPosition - ROUNDING_OFFSET <= 0) {
			setNavButton(buttonPrevious, true);
			setNavButton(buttonNext, false);
			return;
		}

		if (trackPosition + trackVisibleSize + ROUNDING_OFFSET >= trackTotalSize) {
			setNavButton(buttonPrevious, false);
			setNavButton(buttonNext, true);
			return;
		}

		setNavButton(buttonPrevious, false);
		setNavButton(buttonNext, false);
	}

	function positionNavigation() {
		if (!navigation || !navigationAnchorSelector) {
			return;
		}

		const anchor = /** @type {HTMLElement} */ (
			slides[0]?.querySelector(navigationAnchorSelector)
		);

		if (anchor) {
			navigation.style.insetBlockStart = `${anchor.offsetHeight / 2}px`;
		}
	}

	function showPreviousSlide() {
		track.scrollLeft -= getSlideSize();
	}

	function showNextSlide() {
		track.scrollLeft += getSlideSize();
	}

	function onScroll() {
		requestAnimationFrame(setButtonState);
	}

	function onResize() {
		requestAnimationFrame(() => {
			setButtonState();
			positionNavigation();
		});
	}

	buttonPrevious.addEventListener("click", showPreviousSlide, { signal });
	buttonNext.addEventListener("click", showNextSlide, { signal });
	track.addEventListener("scroll", onScroll, { signal });

	/**
	 * ResizeObserver fires once immediately on observe, which sets the initial
	 * button state without needing a separate init call.
	 */
	const resizeObserver = new ResizeObserver(onResize);
	resizeObserver.observe(track);

	let slideObserver = null;

	if (indicatorDots && indicatorDots.length > 0) {
		slideObserver = new IntersectionObserver(
			(entries) => {
				let mostVisible = null;
				let highestRatio = 0;

				for (const entry of entries) {
					if (entry.intersectionRatio > highestRatio) {
						highestRatio = entry.intersectionRatio;
						mostVisible = entry.target;
					}
				}

				if (mostVisible) {
					const slideIndex = Array.prototype.indexOf.call(slides, mostVisible);

					if (slideIndex >= 0) {
						indicatorDots.forEach((dot, index) => {
							dot.classList.toggle("is-active", index === slideIndex);
						});
					}
				}
			},
			{
				root: track,
				threshold: [0.5, 0.75, 1],
			},
		);

		for (const slide of slides) {
			slideObserver.observe(slide);
		}
	}

	return function teardown() {
		controller.abort();
		resizeObserver.disconnect();
		slideObserver?.disconnect();

		if (disableNavAtBoundary) {
			buttonPrevious.disabled = true;
			buttonNext.disabled = false;
		} else {
			buttonPrevious.toggleAttribute("hidden", true);
			buttonNext.toggleAttribute("hidden", false);
		}

		if (navigation) {
			navigation.style.insetBlockStart = "";
		}
	};
}
